import { createSelector, MemoizedSelector, select } from '@ngrx/store';

import { StateSelector } from '@fcom/core/selectors/selector-utils';
import { TravelerService } from '@fcom/dapi';
import {
  FlightSeatSelections,
  SeatMapState,
  SeatMapStatus,
  SeatMapWithStatus,
  SeatSelectionForFlight,
} from '@fcom/common/interfaces/seat-map.interface';
import { entrySet, mapValues } from '@fcom/core/utils/utils';
import { SubCategory } from '@fcom/dapi/api/models/sub-category';
import { SeatmapSvgAndControlData } from '@fcom/dapi/api/models/seatmap-svg-and-control-data';

import { CommonBookingFeatureState, CommonBookingState } from '../store.interface';
import { getSeatPrice } from '../../modules/ancillaries/utils/ancillary.utils';
import { selectCommonBookingState } from './feature.selector';

export const seatMapStateSelector = createSelector(
  selectCommonBookingState,
  (state: CommonBookingState) => state?.seatMap
);

export const isSeatsDirty = createSelector(
  selectCommonBookingState,
  (state: CommonBookingState) => state?.seatsDirty ?? false
);

export const seatMapNonBundledSeatSelectionsByFlightSelector = createSelector(seatMapStateSelector, (state) =>
  mapValues(state?.seatmaps || {}, (v) => v.selections || {})
);

export const seatSelections = createSelector(seatMapStateSelector, (sm: SeatMapState) => {
  if (!sm || !sm.seatmaps) {
    return undefined;
  }
  return mapValues(sm.seatmaps, (s: SeatMapWithStatus, _cartFlightId) =>
    entrySet<SeatSelectionForFlight>(s.selections)
      .reduce<TravelerService[]>(
        (all: TravelerService[], { key: travelerId, value: { seatNumber, characteristics } }) => {
          const seatPrice = getSeatPrice(
            s.seatMap?.seats?.find((seat) => seat.seatNumber === seatNumber),
            travelerId
          );
          return all.concat({
            category: SubCategory.SEAT,
            quantity: seatNumber ? 1 : 0,
            variant: seatNumber,
            seatNumber,
            displayName: '',
            title: '',
            originalPricePerItem: seatPrice?.originalPrice ?? s.selections[travelerId].originalPricePerItem,
            pricePerItem: seatPrice?.price ?? s.selections[travelerId].pricePerItem,
            paid: s.selections[travelerId].paid,
            ticketed: s.selections[travelerId].ticketed,
            modifiable: s.selections[travelerId].modifiable,
            travelerId: travelerId,
            includedInTicketType: false,
            includedInTierBenefit: false,
            seatCharacteristics: characteristics,
          });
        },
        []
      )
      .filter(Boolean)
  );
});

export const seatMapWithStatus = (
  flightId: string
): MemoizedSelector<CommonBookingFeatureState, SeatMapWithStatus | undefined> =>
  createSelector(seatMapStateSelector, (sm: SeatMapState) => sm?.seatmaps?.[flightId]);

/**
 * Selects the seatMap from the state via given flightId.
 */
export const seatMapForFlight = (
  flightId: string
): MemoizedSelector<CommonBookingFeatureState, SeatmapSvgAndControlData | undefined> =>
  createSelector(seatMapWithStatus(flightId), (sm: SeatMapWithStatus | undefined) => sm?.seatMap);

export const seatSelectionsForFlight = (
  flightId: string
): MemoizedSelector<CommonBookingFeatureState, FlightSeatSelections | undefined> =>
  createSelector(seatMapWithStatus(flightId), (sm: SeatMapWithStatus | undefined) => sm?.selections);

export const seatSelectionsForFlightAndTraveler = (
  flightId: string,
  travelerId: string
): StateSelector<CommonBookingFeatureState, SeatSelectionForFlight> =>
  select(createSelector(seatSelectionsForFlight(flightId), (ss: FlightSeatSelections) => ss?.[travelerId]));

export const seatMapForFlightStatus = (
  flightId: string
): MemoizedSelector<CommonBookingFeatureState, SeatMapStatus | undefined> =>
  createSelector(seatMapWithStatus(flightId), (ss: SeatMapWithStatus | undefined) => ss?.status);

export const seatMapStatusByFlightSelector = createSelector(
  seatMapStateSelector,
  (sm: SeatMapState): Record<string, SeatMapStatus> =>
    Object.fromEntries(Object.entries(sm?.seatmaps ?? {}).map(([flightId, { status }]) => [flightId, status]))
);

export const seatLastToken = (): StateSelector<CommonBookingFeatureState, string> =>
  select(createSelector(seatMapStateSelector, (sm: SeatMapState) => sm?.latestToken));

export const originalRegularSeatByFlightSelector = createSelector(
  seatMapStateSelector,
  (state: SeatMapState) => state?.originalRegularSeatByFlight ?? {}
);
